import React, { useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { makeStyles } from '@material-ui/core/styles'
import { cloneDeep } from 'lodash'
import { BarAndParkTable } from '../../components/Tables/BarAndParkTable'
import { Button, Card, CardContent, CardHeader, Typography } from '@material-ui/core'
import {
	getCheckInsByFacilityId,
	removeAccount,
	removePet,
	removeGuest,
	completeCheckIn,
	addNewVisitor,
	getParkingCodes,
	updateParkingCode,
	refreshList
} from '../../actions/checkIns/checkins.actions'
import { getVisitsByFacilityId } from '../../actions/visits/visits.actions'
import { getMembershipTypes, addMembership, updateMembership } from '../../actions/households/households.actions'
import moment from 'moment'
import { useLocation } from 'react-router-dom'
import { isPetsVaccinesValidForFacility } from '../../services/helpers'
import { showError } from '../../actions/misc/misc.actions'
import { DataDialog } from '../../components/Dialogs'
import { api } from '../../services/api'
import { facilityOffersBarPark } from '../../utils/barPark'

const useStyles = makeStyles((theme) => ({
	root: {
		flexGrow: 1
	}
}))

function useQuery() {
	return new URLSearchParams(useLocation().search)
}

const Schedule = (props) => {
	const classes = useStyles()
	const dispatch = useDispatch()
	const query = useQuery()

	let aggregates = {
		'Parties Checked In': {
			value: 0,
			active: false
		},
		'Parties Waiting': {
			value: 0,
			active: false
		},
		spacer: true,
		'Desk Parties': {
			value: 0,
			active: false
		},
		'Fast Lane Parties': {
			value: 0,
			active: false
		}
	}

	const facility = useSelector((state) => state.specific.facilities.selected)
	const facilities = useSelector((state) => state.specific.facilities.facilities)
	const visits = useSelector((state) => state.specific.visits.visits)
	const checkIns = useSelector((state) => state.specific.checkIns.checkIns)
	const membershipTypes = useSelector((state) => state.specific.households.membershipTypes)
	const singleLoading = useSelector((state) => state.specific.checkIns.single_loading)
	const [filteredCheckIns, setFilteredCheckIns] = useState(checkIns)
	const [filter, setFilter] = useState('')
	const [loading, setLoading] = useState(false)
	const [date, setDate] = useState(
		query.get('date') ? moment(query.get('date')).format('ll') : moment().format('ll')
	)

	const [scheduleAggregates, setScheduleAggregates] = useState(aggregates)
	const [petsInFacility, setPetsInFacility] = useState([])
	// const [exportDialog, setExportDialog] = useState(false)

	useEffect(() => {
		dispatch({ type: 'CLEAR_CHECKINS' })
	}, [])

	useEffect(() => {
		if (facility) {
			dispatch(getCheckInsByFacilityId(facility, date))
			if (!visits) dispatch(getVisitsByFacilityId(facility, date))
		}
	}, [date, facility])

	useEffect(() => {
		if (visits) {
			let petsInFac = []
			visits.forEach((visit) => {
				visit.pets.forEach((pet) => {
					if (pet.status && pet.status.checked_in)
						petsInFac.push({ pet_id: pet._id, visitType: visit.type })
				})
			})
			setPetsInFacility(petsInFac)
		}
	}, [visits])

	useEffect(() => {
		if (!facility?._id) return
		dispatch(getMembershipTypes({ facilityId: facility._id }))
	}, [facility?._id])

	useEffect(() => {
		if (checkIns) {
			let updatedAggregates = cloneDeep(scheduleAggregates)

			if (checkIns.length > 0) {
				updatedAggregates['Parties Checked In'].value = getInBar().length
				updatedAggregates['Parties Waiting'].value = getWaiting().length
				updatedAggregates['Desk Parties'].value = getWaiting().length - getFastLane().length
				updatedAggregates['Fast Lane Parties'].value = getFastLane().length

				setScheduleAggregates(updatedAggregates)
				setFilteredCheckIns(checkIns)
			} else {
				for (const key in updatedAggregates) {
					if (updatedAggregates.hasOwnProperty(key)) {
						if (typeof updatedAggregates[key] === 'object') {
							updatedAggregates[key].value = 0
						}
					}
				}

				setScheduleAggregates(updatedAggregates)
				setFilteredCheckIns(checkIns)
			}
		}
	}, [checkIns])

	const getInBar = () => {
		if (!checkIns) return []
		return checkIns.filter((checkin) => checkin.checked_in)
	}

	const getWaiting = () => {
		if (!checkIns) return []
		return checkIns.filter((checkin) => !checkin.checked_in)
	}

	const getFastLane = () => {
		if (!checkIns) return []

		let fast = checkIns.filter((c) => {
			if (c.hasPetIssues) return false
			if (c.checked_in) return false
			if (c.hasIssues) return false
			if (!c.memberships.length && !c.day_passes.length) return false
			
			return true
		})
		return fast
	}

	const getAtDesk = () => {
		if (!checkIns) return []
		return checkIns.filter((c) => {
			if (c.checked_in) return false
			if (c.hasIssues) return true
			if (!c.pets.length) return false
			if (c.hasPetIssues) return true
			if (!c.memberships.length && !c.day_passes.length) return true
			return false
		})
	}

	const setAggregateActive = (aggregate) => {
		let aggregates = Object.keys(scheduleAggregates)
		let wasActive = scheduleAggregates[aggregate].active

		aggregates.forEach((aggregate) => {
			if (!aggregate.includes('spacer')) {
				scheduleAggregates[aggregate].active = false
			}
		})

		if (!wasActive) {
			scheduleAggregates[aggregate].active = true
			setFilter(aggregate)
		} else {
			setFilter('')
		}
	}

	const peopleCheckedIn = () => {
		if (!checkIns || !checkIns.length) return 0
		let num = []
		checkIns
			.filter((c) => c.checked_in)
			.forEach((c, i) => {
				c.account_ids.forEach((id, i) => {
					num.push(id)
				})
				c.guests.forEach((g, i) => {
					num.push(g)
				})
			})
		return num.length
	}

	const dogsCheckedIn = () => {
		if (!checkIns || !checkIns.length) return 0
		let num = []
		checkIns
			.filter((c) => c.checked_in)
			.forEach((c, i) => {
				c.pet_ids.forEach((id, i) => {
					num.push(id)
				})
			})
		return num.length
	}

	useEffect(() => {
		if (checkIns) {
			switch (filter) {
				case 'Parties Checked In':
					setFilteredCheckIns(getInBar())
					break
				case 'Parties Waiting':
					setFilteredCheckIns(getWaiting())
					break
				case 'Desk Parties':
					setFilteredCheckIns(getAtDesk())
					break
				case 'Fast Lane Parties':
					setFilteredCheckIns(getFastLane())
					break
				default:
					setFilteredCheckIns(checkIns)
					break
			}
		}
	}, [filter, checkIns])

	const changeDate = (type) => {
		if (type === 'add') {
			setDate(moment(date).add(1, 'd').format('ll'))
		} else if (type === 'subtract') {
			setDate(moment(date).subtract(1, 'd').format('ll'))
		}
	}

	const getRoster = async (args) => {
		try {
			setLoading(true)
			let dataStr = JSON.stringify({ ...args })

			console.log('data str...', dataStr)

			let { data } = await api({
				url: `${process.env.REACT_APP_FIREBASE_URL}/roster`,
				method: 'POST',
				headers: {
					'Content-Type': 'application/x-www-form-urlencoded'
				},
				data: `data=${dataStr}`
			})

			if (data.noData) {
				alert('No data for date range')
				setLoading(false)
				setExportDialog(false)
				return
			}

			setTimeout(() => {
				window.open(data.link, '_blank')
				setLoading(false)
				setExportDialog(false)
			}, 10 * 1000)
		} catch (e) {
			console.log('huge e...', e)
			setLoading(false)
			dispatch(showError('Error fetching data'))
		}
	}

	if (facility && !facilityOffersBarPark(facility)) {
		return (
			<div className={classes.root}>
				<Card style={{ maxWidth: '1245px', margin: 24 }}>
					<CardHeader title="Bar & Park" />
					<CardContent>
						<Typography variant="body2" color="textSecondary">
							{facility.name} does not offer Bar & Park, so check-ins cannot be created here.
						</Typography>
					</CardContent>
				</Card>
			</div>
		)
	}

	return (
		<div className={classes.root}>
			{facility ? (
				<div className={classes.visitsTableContainer}>
					<BarAndParkTable
						date={date}
						changeDate={changeDate}
						setDate={setDate}
						peopleCheckedIn={peopleCheckedIn()}
						dogsCheckedIn={dogsCheckedIn()}
						membershipTypes={membershipTypes}
						facility={facility}
						scheduleAggregates={scheduleAggregates}
						checkIns={filteredCheckIns}
						setAggregateActive={setAggregateActive}
						singleLoading={singleLoading}
						removeAccount={(obj) => dispatch(removeAccount(obj))}
						removePet={(obj) => dispatch(removePet(obj))}
						removeGuest={(obj) => dispatch(removeGuest(obj))}
						addNewVisitor={(obj) => dispatch(addNewVisitor(obj))}
						completeCheckIn={(obj) => {
							dispatch(completeCheckIn(obj))
							if (obj.parking_code) dispatch(updateParkingCode(obj))
						}}
						refreshList={() => dispatch(getCheckInsByFacilityId(facility, date))}
						petsInFacility={petsInFacility}
					/>
					{/*<div
						style={{
							display: 'flex',
							flexDirection: 'row',
							justifyContent: 'flex-end',
							padding: 24,
							width: '100%'
						}}>
						<Button variant="contained" color="secondary" onClick={() => setExportDialog(true)}>
							EXPORT BAR MEMBER ROSTER
						</Button>
					</div>*/}
				</div>
			) : (
				<h3>Please Select a Facility</h3>
			)}
			{/*<DataDialog
				isShown={exportDialog}
				loading={loading}
				type="barRoster"
				setIsShown={() => setExportDialog(!exportDialog)}
				runExport={getRoster}
				noDates={true}
				facilities={facility ? [facility] : []}
			/>*/}
		</div>
	)
}

export default Schedule
